import { AppType, DestinationWindow } from "client/Api/Apps/common";
import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../useRenderManager";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TileNavigationOptions, useGo } from "./useGo";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation,
} from "./useInvokeNavigation";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileRenderState } from "../RenderState/common";
import { TileContextProvider } from "../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type ShortcutTileContentProps = {
	renderState: TileRenderState<AppType.Url>;
	onEdit: (() => void) | undefined;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const ShortcutTileContent = (props: ShortcutTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		userTimezone,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useShortcutTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
ShortcutTileContent.displayName = "ShortcutTileContent";

export function useShortcutTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Url>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	userTimezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const {
		conditionalAccess,
		maintenanceWindow,
		settings: {
			destinationWindow
		}
	} = renderStateIsh.data;

	const go: (options: TileNavigationOptions) => void = useGo(renderStateIsh);

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useConditionalAccessNavigation(
			conditionalAccess,
			useMaintenanceWindowNavigation(
				maintenanceWindow,
				userTimezone,
				useCallback(
					(): void => {
						// We are not using navigateToShortcutApp here as the configuration can be on tile level
						go({
							inNewWindow: destinationWindow === DestinationWindow.NewWindow
						});
					},
					[go, destinationWindow]
				)
			)
		)
	);
}
